import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Navbar } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Briefcase, TrendingUp } from "lucide-react";
import API from "@/utils/api";

const DreamOption = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dreamOption, setDreamOption] = useState("");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!storedUser || !token) {
      navigate("/login");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    if (userData.dream_option) {
      setDreamOption(userData.dream_option);
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dreamOption) {
      alert("Please select your dream option");
      return;
    }
    
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      await axios.post(`${API}/dream-option`, { dream_option: dreamOption }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedUser = { ...user, dream_option: dreamOption, dream_submitted: true };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      alert("Your dream option has been saved!");
      navigate("/dashboard");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to save dream option");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-transparent py-8 px-4" data-testid="dream-option-page">
      <Navbar />
      <div className="max-w-xl mx-auto pt-20">
        <Card className="rounded-2xl shadow-xl">
          <CardHeader className="text-center border-b">
            <Link to="/" className="flex items-center justify-center gap-2 mb-4">
              <img src="/images/logo.png" alt="NokMechik" className="w-8 h-8 rounded-full object-cover" />
              <span className="text-2xl font-bold text-slate-800">NokMechik</span>
            </Link>
            <CardTitle className="text-2xl">What is Your Dream?</CardTitle>
            <CardDescription>Tell us about your career aspirations</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <Label className="text-lg font-medium">Choose your path:</Label>
                
                <div 
                  onClick={() => setDreamOption("employed")}
                  className={`p-6 border-2 rounded-2xl cursor-pointer transition-all ${
                    dreamOption === "employed" 
                      ? "border-terracotta bg-terracotta/5" 
                      : "border-slate-200 hover:border-terracotta/50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      dreamOption === "employed" ? "bg-terracotta text-white" : "bg-slate-100"
                    }`}>
                      <Briefcase className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">I Want to be Employed</h3>
                      <p className="text-slate-600 text-sm">Looking for job opportunities in organizations</p>
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => setDreamOption("self_employed")}
                  className={`p-6 border-2 rounded-2xl cursor-pointer transition-all ${
                    dreamOption === "self_employed" 
                      ? "border-teal-600 bg-teal-50" 
                      : "border-slate-200 hover:border-teal-500/50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      dreamOption === "self_employed" ? "bg-teal-600 text-white" : "bg-slate-100"
                    }`}>
                      <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">I Want to be Self-Employed</h3>
                      <p className="text-slate-600 text-sm">Start my own business or entrepreneurship</p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-terracotta hover:bg-terracotta/90 rounded-full py-6"
                disabled={loading || !dreamOption}
              >
                {loading ? "Saving..." : "Save My Dream"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DreamOption;
